import { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import {
  Button,
  Heading,
  InfoBox,
  Markdown,
  Typography,
} from '@altify/components';
import { useContent } from '@altify/utils';
import { content } from './AiInsights.content';
import { useAskQuestionRequest } from './hooks/useAskQuestionRequest';
import { useStreamedSteps } from './hooks/useStreamedSteps';
import clsx from 'clsx';
import { extractPlainTextFromDrawer } from './utils/markdown';
import { CopyButton } from './components/CopyButton';
import { DeepDiveContent } from './components/DeepDiveContent';
import { LoadingSteps } from './components/LoadingSteps';
import { QuestionBox } from './components/QuestionBox';
import { SummaryBox } from './components/SummaryBox';
import { MARKDOWN_STYLES } from './constants/styles';
import { AiInsightsHeader } from './components/AiInsightsHeader';
import { AiInsightsQuestionsList } from './AiInsightsQuestionsList';

export interface AiInsightsContentProps {
  pageName: 'Portfolio' | 'Assets' | 'Explore';
  question: string;
  onClose: () => void;
  setAskedQuestion: (question: string) => void;

  isFullPage?: boolean;
  productType?: string;
  productCode?: string;
}

export function AiInsightsContent({
  question,
  pageName,
  onClose,
  isFullPage = false,
  productType,
  productCode,
  setAskedQuestion,
}: AiInsightsContentProps) {
  const localizedContent = useContent(content);
  const [copied, setCopied] = useState(false);

  const {
    questionData,
    isLoading: isAskingQuestion,
    refetch,
    productCodesAndNames,
  } = useAskQuestionRequest({ question, pageName });

  const { streamedSteps } = useStreamedSteps(isAskingQuestion);

  const assetAnalysis = questionData?.finalJson?.asset_analysis;
  const summaryPoints = assetAnalysis?.tldr_section?.points ?? [];
  const summaryText = assetAnalysis?.tldr_section?.summary ?? '';
  const deepDivePoints = assetAnalysis?.deep_dive_sections ?? [];
  const conclusion = assetAnalysis?.analyst_conclusion ?? '';

  const handleCopy = async () => {
    const plainText = extractPlainTextFromDrawer(
      summaryText,
      summaryPoints,
      deepDivePoints,
      conclusion,
    );
    try {
      await navigator.clipboard.writeText(plainText);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // clipboard write failed — silently ignore
    }
  };

  return (
    <AnimatePresence>
      <motion.aside
        key="vertical-drawer-panel"
        className={clsx(
          'fixed right-0 top-0 z-50 flex h-full flex-col gap-4 bg-background text-white',
          isFullPage ? 'w-full' : 'w-[330px]',
        )}
        initial={{ x: '100%' }}
        animate={{ x: 0 }}
        exit={{ x: '100%' }}
        transition={{ type: 'spring', stiffness: 320, damping: 32 }}>
        <div className="flex flex-col px-4 py-5 gap-4">
          <div className="flex flex-col shrink-0 gap-4">
            <AiInsightsHeader onClose={onClose} />
          </div>

          <div className="flex flex-col h-full gap-4 overflow-y-auto">
            <QuestionBox questionText={question} isLoading={isAskingQuestion} />
            <AnimatePresence mode="wait">
              {isAskingQuestion ? (
                <motion.div
                  key="loading"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.3 }}>
                  <LoadingSteps
                    steps={streamedSteps}
                    productCodesAndNames={productCodesAndNames}
                  />
                </motion.div>
              ) : (
                <motion.div
                  key="content"
                  className="flex flex-col shrink-0 gap-4"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.3 }}>
                  {!questionData?.finalJson ? (
                    <>
                      <Typography size="medium" className="text-neutral-300">
                        {localizedContent.errorLoadInsights}
                      </Typography>
                      <Button
                        text={localizedContent.reloadInsights}
                        size="medium"
                        disabled={isAskingQuestion}
                        onPress={() => {
                          refetch();
                        }}
                      />
                    </>
                  ) : (
                    <>
                      <Typography size="medium" className="text-neutral-300 mt-2">
                        {localizedContent.researchedFor.replace(
                          '{{seconds}}',
                          questionData?.finalJson?.processing_time_seconds?.toFixed(
                            0,
                          ) ?? '0',
                        )}
                      </Typography>
                      <SummaryBox
                        summary={summaryText}
                        points={summaryPoints}
                      />
                      <InfoBox subtitle={localizedContent.disclaimerText} />
                      <Heading level={5}>{localizedContent.deepDive}</Heading>
                      <DeepDiveContent sections={deepDivePoints} />
                      <Heading level={5}>{localizedContent.conclusion}</Heading>
                      <Markdown styles={MARKDOWN_STYLES}>{conclusion}</Markdown>
                      {!isAskingQuestion && (
                        <div className="py-2 flex flex-col items-start gap-4">
                          <CopyButton onCopy={handleCopy} copied={copied} />
                          <div className="flex w-full border border-neutral-400" />
                          <AiInsightsQuestionsList
                            pageName={pageName}
                            productType={productType}
                            productCode={productCode}
                            excludedQuestion={question}
                            onPress={question => {
                              setAskedQuestion?.(question);
                            }}
                          />
                        </div>
                      )}
                    </>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </motion.aside>
    </AnimatePresence>
  );
}
